import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { OkButton } from 'src/components/Buttons/OkButton';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { useEventHubContext } from 'src/contexts/EventHubContext';
import { vars } from 'src/core/themes';
import { Switch, Tooltip } from 'src/core/ui';
import { Stack } from 'src/core/ui';
import { useAutoBroadcastSellerAccountSettings } from 'src/hooks/api/useAutoBroadcastSellerAccountSettings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useListingAutoPricingSettings } from 'src/modals/ListingDetails/components/useListingAutoPricingSettings';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { tryAutoBroadcastListing } from 'src/utils/autoBroadcastUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  Feature,
  Listing,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdates,
  ListingDetailsUpdateInput,
} from 'src/WebApiController';

import { AutoPricingCompQuantitiesSelector } from '../AutoPricingCompQuantitiesSelector/AutoPricingCompQuantitiesSelector';
import { AutoPricingSectionRowIdFilter } from '../components/AutoPricingSectionRowIdFilter';

type SimplifiedAutopricingSectionProps = {
  listing: Listing | undefined;
};

export const SimplifiedAutopricingSection = ({
  listing,
}: SimplifiedAutopricingSectionProps) => {
  // Hooks
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSections, setShowSections] = useState(false);

  const { showErrorDialog } = useClientLogContext();

  const { activeAccountWebClientConfig } = useAppContext();

  // React-hook-form
  const { setValue, getValues, reset } =
    useFormContext<ListingDetailsUpdateInput>();

  const { hasChanges, autoPricingEnabled, canAutoPrice } =
    useListingAutoPricingSettings(listing);
  const { data: autoBroadcastSettings } =
    useAutoBroadcastSellerAccountSettings();
  const hasAutoBroadcastFeature = useUserHasFeature(
    Feature.AutoBroadcastWhenPricing
  );

  // Mutations
  const {
    updateItemInEvent,
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { onUpdateListing } = useEventHubContext();

  const submitPricingSettingsChange = useCallback(() => {
    setIsSubmitting(true);
    const listForm = getValues();

    if (!listing) {
      return;
    }

    tryInvokeApi(
      async () => {
        const client = new ListingClient(activeAccountWebClientConfig);

        const input: ListingDetailsAutoPricingSectionUpdates = {
          ...listForm,
          id: listing.id,
          rowVersion: null,
          autoPriceFrequency: null,
          compListingQuantityFilters: null,
          compListingQuantityScoreAdjustmentOverrides: null,
          scoreModel: null,
          autoPricingDefaultQuantityFiltersJson: null,
          venueZoneOverrideId: null,
          venueZoneConfigType: null,
          sectionRowScoreRecommendations: null,
        };

        await client.updateListingAutoPricingSectionByListingId(
          input.id,
          input
        );

        // Auto-broadcast when autopricing is toggled ON
        if (
          listForm.autoPricingEnabled === true &&
          listing.isAutoPrc !== true
        ) {
          try {
            await tryAutoBroadcastListing({
              client,
              listingId: listing.id,
              listingStatus: listing.status,
              effectivePrice: listing.listPrice,
              autoBroadcastEnabled:
                autoBroadcastSettings?.autoBroadcastManuallyPricedListings,
              hasFeature: hasAutoBroadcastFeature,
            });
          } catch {
            // Broadcast failures should not break the pricing settings update flow
          }
        }

        // Refersh listing
        const updated = await client.getListingByListingId(
          listing.id,
          listing as ListingDetails,
          [
            ListingDetailDataField.Pricing, // To refresh pricing related listing fields, i.e. auto pricing enabled
            ListingDetailDataField.AutoPricing,
          ]
        );
        if (updated) {
          updateItemInEvent(updated, ActionOutboxEntityType.Listing);
          onUpdateListing(updated.id);
        }
      },
      (error) => {
        showErrorDialog(
          'ListingClient.updateListingAutoPricingSection',
          error,
          {
            trackErrorData: {
              item1: listing.id,
              item2: listForm,
            },
          }
        );
      },
      () => {
        setIsSubmitting(false);
        reset(listForm);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getValues,
    listing,
    autoBroadcastSettings,
    activeAccountWebClientConfig,
    refreshExpandedListItems,
    showErrorDialog,
  ]);

  const showSave = listing != null && hasChanges(getValues());

  return canAutoPrice && listing ? (
    <Stack direction="column" gap="m" alignItems="start" width="full">
      <Stack gap="l" alignItems="center" width="full">
        <Stack gap="s" alignItems="center">
          <Content id={ContentId.QuantityAbbreviated} />

          <AutoPricingCompQuantitiesSelector
            disabled={false}
            rootStyle={{ width: '90px' }}
          />
        </Stack>
        <Stack
          gap="s"
          alignItems="center"
          style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
        >
          <Tooltip
            triggerContent={
              <Stack gap="s" alignItems="center">
                <Content id={ContentId.ShowSections} />
                <InfoOutlineIcon withHoverEffect size={vars.iconSize.s} />
              </Stack>
            }
            contentVariant="dark"
          >
            <Content id={ContentId.ShowSectionsInfo} />
          </Tooltip>
          <Switch
            disabled={listing == null || isSubmitting}
            checked={showSections}
            onChange={(e) => e.stopPropagation()}
            onCheckedChange={(isChecked) => {
              setShowSections(isChecked);
            }}
          />
        </Stack>
        <Stack gap="s" alignItems="center">
          <Tooltip
            triggerContent={
              <Stack gap="s" alignItems="center">
                <Content id={ContentId.Autopricing} />
                <InfoOutlineIcon withHoverEffect size={vars.iconSize.s} />
              </Stack>
            }
            contentVariant="dark"
          >
            <Content id={ContentId.AutopricingInfo} />
          </Tooltip>
          <Switch
            disabled={listing == null || isSubmitting}
            checked={Boolean(autoPricingEnabled)}
            onChange={(e) => e.stopPropagation()}
            onCheckedChange={(isChecked) => {
              setValue('autoPricingEnabled', isChecked);
            }}
          />
        </Stack>
        {showSave && (
          <OkButton
            onClick={() => {
              submitPricingSettingsChange();
            }}
            textContentId={ContentId.Save}
            disabled={isSubmitting}
          />
        )}
      </Stack>

      {showSections && (
        <AutoPricingSectionRowIdFilter
          listing={listing}
          disabled={listing == null || isSubmitting}
        />
      )}
    </Stack>
  ) : null;
};
